import { Sparkles, Search, FileText } from "lucide-react"
import type { ComponentType } from "react"
type LucideIcon = ComponentType<{ className?: string }>

// ── Context system ──────────────────────────────────────────────────────────────
// The assistant adapts its persona, greeting and suggested questions to the
// page the visitor is on. A "context indicator" is a typed input that the
// frontend resolves at runtime — today only `url` exists, but the union is the
// extension point: add `{ type: "user" }`, `{ type: "product" }`, etc. later
// and merge their greetings in `resolveCopilotContext`.
//
// The goal on the landing page is a sales/support hand-off flow:
//   1. greet the visitor
//   2. ask about their business (buy vs. export/sell)
//   3. follow up about the project they want to work on
//   4. once convinced, jump to the register/signup page and continue the
//      conversation authenticated.

export type ContextIndicator =
  | { type: "url"; pathname: string; search?: Record<string, string> }
  // Future indicators e.g.:
  // | { type: "auth"; role: "BUYER" | "SELLER" | "ADMIN" | "GUEST" }
  // | { type: "product"; id: string }

export interface GreetingSuggestion {
  id: string
  label: string
  prompt: string
  icon?: LucideIcon
}

export interface PageContext {
  /** Stable id used to name the auto-conversation. */
  id: string
  /** Short persona line shown in the chat header tooltip. */
  label: string
  /** The assistant's introduction + qualifying question. */
  greeting: string
  /** Additional message rendered under the greeting. */
  intro?: string
  /**
   * Quick replies shown under the greeting. Clicking one sends its `prompt`
   * to the LLM, which picks up the follow-up conversation from there.
   */
  suggestions: GreetingSuggestion[]
  /**
   * When true the greeting hints that continuing deeper work (submitting an
   * RFQ, selling) needs an account, and the suggestion set is extended with a
   * "Get started" CTA that jumps to the signup page.
   */
  guestFunnel?: boolean
  /**
   * Fallback suggestions used when this page has no custom suggestions.
   * Inferred automatically; don't set manually.
   */
}

interface ContextDefinition {
  test: (pathname: string, search: Record<string, string>) => boolean
  context: Omit<PageContext, "suggestions"> & { suggestions: Omit<GreetingSuggestion, "id">[] }
}

const HOME_SUGGESTIONS = [
  { label: "I want to buy products", prompt: "I'm looking to buy products. Can you help me find verified sellers?" },
  { label: "I want to sell or export", prompt: "I want to sell or export my products. How can Enga help me?" },
  { label: "Tell me how Enga works", prompt: "How does Enga work for buyers and sellers?" },
]

const DEFAULT_SUGGESTIONS = [
  {
    label: "Find suppliers",
    prompt: "Show me some verified suppliers on Enga.",
    icon: Search,
  },
  {
    label: "Draft an RFQ",
    prompt: "Help me structure an RFQ for the products I need.",
    icon: FileText,
  },
  {
    label: "How Enga works",
    prompt: "Give me a quick overview of how Enga works.",
    icon: Sparkles,
  },
]

const CONTEXT_DEFINITIONS: ContextDefinition[] = [
  {
    // Public landing page — the primary sales / support entry point.
    test: (pathname) => pathname === "/",
    context: {
      id: "home",
      label: "Sales & onboarding assistant",
      greeting: "Hi, Enga's AI sales assistant.",
      intro:
        "Welcome! Quick question - are you interested in **buying products** from verified suppliers, or are you looking to **sell or export** your own products? Tell me a little about your business and I'll point you to the right tools.",
      guestFunnel: true,
      suggestions: HOME_SUGGESTIONS,
    },
  },
  {
    test: (pathname) => pathname.startsWith("/explore") || pathname.startsWith("/browse-products") || pathname.startsWith("/browse-supplier"),
    context: {
      id: "browse",
      label: "Sourcing assistant",
      greeting: "Browsing for products? 🧭",
      intro: "Tell me what you're looking for and I'll help you compare verified suppliers and draft a request for quotation.",
      guestFunnel: true,
      suggestions: [
        { label: "Browse by category", prompt: "What product categories can I browse on Enga?" },
        { label: "Compare sellers", prompt: "How do I compare two suppliers I'm interested in?" },
        { label: "Find a specific product", prompt: "Help me find a supplier for the products I need." },
      ],
    },
  },
  {
    test: (pathname) => pathname.startsWith("/logistics"),
    context: {
      id: "logistics",
      label: "Logistics assistant",
      greeting: "Planning a shipment? 🚚",
      intro: "I can help you pick the right shipping mode and understand rates for your route.",
      suggestions: [
        { label: "Compare shipping modes", prompt: "Compare air, sea and road shipping for my shipment." },
        { label: "Rates on a route", prompt: "What are typical freight rates from India to the US?" },
      ],
    },
  },
  {
    test: (pathname) => pathname.startsWith("/buyer-dashboard"),
    context: {
      id: "buyer-dashboard",
      label: "Buyer support",
      greeting: "Need a hand with your buyer workflow? 🛒",
      intro: "I can help you manage RFQs, quotations and orders.",
      suggestions: [
        { label: "Status of my RFQs", prompt: "Show me the status of my active RFQs." },
        { label: "Compare quotations", prompt: "Help me compare the quotations I've received." },
        { label: "Track an order", prompt: "How do I track my latest order?" },
      ],
    },
  },
  {
    test: (pathname) => pathname.startsWith("/seller-dashboard"),
    context: {
      id: "seller-dashboard",
      label: "Seller support",
      greeting: "Need a hand selling on Enga? 📦",
      intro: "I can help you manage your offers, RFQs and shipments.",
      suggestions: [
        { label: "Incoming queries", prompt: "Show me my incoming RFQ requests." },
        { label: "Update my catalogue", prompt: "How do I update my product catalogue?" },
        { label: "Shipment status", prompt: "What's the status of my open shipments?" },
      ],
    },
  },
  {
    test: (pathname) => pathname.startsWith("/admin-dashboard"),
    context: {
      id: "admin-dashboard",
      label: "Admin support",
      greeting: "Admin suite, at your service. 🛠️",
      intro: "Review pending verifications, quotations and complaints right from here.",
      suggestions: [
        { label: "Pending verifications", prompt: "Show me companies waiting for verification." },
        { label: "Review quotations", prompt: "List quotations awaiting my review." },
        { label: "Open complaints", prompt: "Show me open complaints." },
      ],
    },
  },
]

function normalizePath(pathname: string): string {
  return pathname.replace(/\/+$/, "") || "/"
}

/** Build quick-reply chips for a single context definition. */
function buildSuggestions(
  def: ContextDefinition,
  guestFunnel: boolean,
  authenticated: boolean
): GreetingSuggestion[] {
  const base = def.context.suggestions.map<GreetingSuggestion>((s, i) => ({
    id: `${def.context.id}-s${i}`,
    label: s.label,
    prompt: s.prompt,
    icon: s.icon,
  }))

  let out = base
  if (guestFunnel && !authenticated) {
    out = [
      ...base,
      {
        id: `${def.context.id}-signup`,
        label: "Get started ↗",
        prompt: "I'd like to create an Enga account to get started.",
      },
    ]
  }
  return out
}

/**
 * Resolve the assistant context for a set of context indicators. Currently only
 * `url` is consumed; future indicator types join here and merge their context.
 */
export function resolveCopilotContext(
  indicators: ContextIndicator[],
  options: { authenticated?: boolean } = {}
): PageContext {
  const authenticated = options.authenticated ?? false
  let matched: ContextDefinition | undefined

  for (const indicator of indicators) {
    if (indicator.type !== "url") continue
    const pathname = normalizePath(indicator.pathname)
    const search = indicator.search ?? {}
    matched = CONTEXT_DEFINITIONS.find((def) => def.test(pathname, search))
    if (matched) break
  }

  if (!matched) {
    return {
      id: "default",
      label: "Enga AI assistant",
      greeting: "Hi, How can I help you today?",
      suggestions: buildSuggestions(
        { test: () => true, context: { id: "default", label: "Enga AI assistant", greeting: "", suggestions: DEFAULT_SUGGESTIONS } },
        false,
        authenticated
      ),
    }
  }

  return {
    id: matched.context.id,
    label: matched.context.label,
    greeting: matched.context.greeting,
    intro: matched.context.intro,
    guestFunnel: matched.context.guestFunnel,
    suggestions: buildSuggestions(matched, matched.context.guestFunnel ?? false, authenticated),
  }
}

export const SIGNUP_PATH = "/signup"